// CORE
import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from "luxon";

// MUI
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';

// DATA
import { useSelector } from 'react-redux';
import { dateTimeFromTxn } from 'data/transactions/utils';
import { getAccountsById } from 'data/accounts/selectors';

// CUSTOM COMPONENTS
import { formatNumber } from 'components/QuickenControls/AmountField';
import UpcomingMenu from './UpcomingMenu';


const useStyles = makeStyles((theme) => ({
  root: ({past}) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: past ? '12px 36px 12px 0' : '12px 16px',
    backgroundColor: theme.palette.greyScale[7],
    border: `1px solid ${theme.palette.greyScale[7]}`,
    borderBottom: past && `1px solid ${theme.palette.greyScale[5]}`,
    height: 78,
    overflow: 'hidden',
    borderRadius: past ? 0 : 8,
    boxShadow: !past && `0px 4px 8px ${theme.palette.color7.opacity100}`,
    transition: 'all ease 240ms',
    '&:hover': {
      borderBottom: past && `1px solid ${theme.palette.greyScale[4]}`,
      boxShadow: !past && `0px 4px 10px 2px ${theme.palette.color2.opacity50}`,
    },
  }),
  grey: {
    color: theme.palette.greyScale[2],
  },
  light: {
    height: 32,
    width: 32,
    padding: 4,
    color: theme.palette.greyScale[4],
    '&:hover': {
      color: theme.palette.greyScale[2],
    },
  },
  bold: {
    fontWeight: 500,
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexGrow: 2,
    overflow: 'hidden',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end',
    flexGrow: 0,
    flexShrink: 0,
  },
  avatar: ({isIncome, past}) => ({
    width: 48,
    height: 48,
    borderRadius: 8,
    fontWeight: 500,
    flexGrow: 0,
    marginRight: 16,
    color: past ? theme.palette.greyScale[1] : theme.palette.greyScale[7],
    backgroundColor: past ? theme.palette.color7.opacity30 : (isIncome && theme.palette.others.positive || theme.palette.color1.opacity100),
  }),
  amount: ({positive}) => ({
    color: `${positive ? theme.palette.others.positive : theme.palette.greyScale[0]} !important`,
    width: '100%',
  }),
  menu: {
    marginLeft: 8,
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.greyScale[5],
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 162,
  },
  status: {
    borderRadius: 12,
    padding: '0 12px',
    color: theme.palette.greyScale[0],
    backgroundColor: theme.palette.color5.opacity30,
    height: 24,
    lineHeight: '24px',
  },
}));


// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                   Row Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ViewRow(props) {
  const { past, txn, isIncome } = props;
  const { payee, amount, id, accountId } = txn;

  const positive = Number(amount) > 0;
  const classes = useStyles({isIncome, positive, past});

  const accountsById = useSelector(getAccountsById);
  const account = accountsById && accountsById.get(accountId);

  const today = DateTime.local().startOf('day');
  const txnDate = dateTimeFromTxn(props.txn);
  const dayDiff = txnDate.diff(today, 'days').days;

  // UI STUFF BELOW
  // # -------------------------------------------
  // date formatting for render
  let dayString = "someday";
  const absDiff = Math.abs(dayDiff);
  switch (dayDiff) {
    case 0:
      dayString = 'today';
      break;
    case -1:
      dayString = 'yesterday';
      break;
    case 1:
      dayString = 'tomorrow';
      break;
    default:
      if (absDiff > 6) {
        dayString = `on ${txnDate.toFormat('LLL d')}`;
      } else {
        dayString = dayDiff > 0 ? `In ${dayDiff} days` : `${absDiff} days ago`;
      }
      break;
  }
  // render return
  return (
    <div className={classes.root} key={id}>
      <Avatar className={classes.avatar}>
        {payee ? payee[0].toUpperCase() : '!'}
      </Avatar>
      <div className={classes.leftColumn}>
        <Typography variant="subtitle1" noWrap className={classes.amount}>
          {formatNumber(amount, 'USD', '0,0.00', true)}
        </Typography>
        <Typography variant="body2" className={classes.grey}>
          {account ? account.name : 'Somewhere'}
        </Typography>
      </div>
      <div className={classes.rightColumn}>
        <Typography variant="body2" className={classes.grey}>
          {dayString}
        </Typography>

        {(txn.source === 'SCHEDULED_TRANSACTION_PENDING' && dayDiff < 0) &&
          <Typography variant="caption" className={classes.status}>
            Past
          </Typography>
        }
      </div>

      {!past &&
        <UpcomingMenu txn={txn} showView={false}/>
      }
    </div>
  );
}

ViewRow.defaultProps = {
  txn: {},
  past: false,
  isIncome: false,
};

ViewRow.propTypes = {
  txn: PropTypes.object,
  past: PropTypes.bool,
  isIncome: PropTypes.bool,
};

export default ViewRow;
